import React from 'react';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Box,
	Typography,
	Chip,
	Divider,
} from '@mui/material';
import { UserNotificationRecord } from './NotificationDialog';

interface Props {
	open: boolean;
	onClose: () => void;
	notification: UserNotificationRecord | null;
}

const CHANNEL_LABELS: Record<string, string> = {
	email: 'Email',
	gsm: 'GSM Text',
	whatsapp: 'WhatsApp',
};

const Field: React.FC<{ label: string; value?: string; multiline?: boolean }> = ({
	label,
	value,
	multiline,
}) => (
	<Box mb={1} sx={{ minWidth: 180, flex: multiline ? '1 1 100%' : undefined }}>
		<Typography variant="caption" color="text.secondary" display="block">
			{label}
		</Typography>
		<Typography
			variant="body2"
			sx={multiline ? { whiteSpace: 'pre-wrap', wordBreak: 'break-word' } : { wordBreak: 'break-word' }}
		>
			{value || '—'}
		</Typography>
	</Box>
);

const formatDate = (value?: string) => {
	if (!value) return undefined;
	try {
		return new Date(value).toLocaleString();
	} catch {
		return value;
	}
};

export const NotificationViewDialog: React.FC<Props> = ({ open, onClose, notification }) => {
	if (!notification) return null;

	const channelLabel = CHANNEL_LABELS[notification.channel] || notification.channel;

	return (
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
			<DialogTitle>
				<Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
					Notification Details
					<Chip
						label={notification.status}
						size="small"
						color={notification.status === 'active' ? 'success' : 'warning'}
						sx={{ fontWeight: 600, textTransform: 'capitalize' }}
					/>
					<Chip
						label={channelLabel}
						size="small"
						sx={{
							fontWeight: 600,
							color: '#fff',
							...(notification.channel === 'gsm'
								? { background: 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)' }
								: notification.channel === 'whatsapp'
									? { bgcolor: 'success.main' }
									: { bgcolor: 'warning.main' }),
						}}
					/>
				</Box>
			</DialogTitle>
			<DialogContent dividers>
				<Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
					Core Settings
				</Typography>
				<Box display="flex" gap={4} flexWrap="wrap" mb={2}>
					<Field label="Name" value={notification.name} />
					<Field label="Event Type" value={notification.eventType} />
					<Field label="Channel" value={channelLabel} />
					<Field label="Status" value={notification.status} />
				</Box>

				<Divider sx={{ my: 2 }} />
				<Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
					Message
				</Typography>
				<Box sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 2, mb: 2 }}>
					<Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
						{notification.message || '—'}
					</Typography>
				</Box>

				<Divider sx={{ my: 2 }} />
				<Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
					Audit
				</Typography>
				<Box display="flex" gap={4} flexWrap="wrap">
					<Field label="Created By" value={notification.createdBy} />
					<Field label="Updated By" value={notification.updatedBy} />
					<Field label="Created At" value={formatDate(notification.createdAt)} />
					<Field label="Updated At" value={formatDate(notification.updatedAt)} />
				</Box>
			</DialogContent>
			<DialogActions>
				<Button variant="outlined" color="secondary" onClick={onClose}>
					Close
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default NotificationViewDialog;
